import { extent, scaleBand, scaleLinear } from 'd3';

// lodash
import { includes, isArray, isFinite, size, sum } from 'lodash';

export const roundRange = ({ range, padMin, padMax }) => {
  const ROUND_TO = 10;

  const result = [
    Math.max(0,(Math.floor(range[0]/ROUND_TO)) * ROUND_TO - padMin),
    (Math.ceil(range[1]/ROUND_TO)) * ROUND_TO + padMax
  ];

  return result;
};

export const minMax = (axis) => {
  return roundRange({ range: extent(axis.valueRange), padMin: axis.pad[0], padMax: axis.pad[1] });
};


// ganked from here:
// http://stackoverflow.com/a/13505624
export const roundRect = ({
  x,
  y,
  width,
  height,
  radius = 4,
  corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
}) => {
  let configString;

  if (isFinite(x) && isFinite(y) && width && height) {
    configString = 'M' + (x + radius) + ',' + y;
    configString += 'h' + (width - 2*radius);
    if (includes(corners, 'top-right')) {
      configString += 'a' + radius + ',' + radius + ' 0 0 1 ' + radius + ',' + radius;
    } else {
      configString += 'h' + radius; configString += 'v' + radius;
    }
    configString += 'v' + (height - 2*radius);
    if (includes(corners, 'bottom-right')) {
      configString += 'a' + radius + ',' + radius + ' 0 0 1 ' + -radius + ',' + radius;
    } else {
      configString += 'v' + radius; configString += 'h' + -radius;
    }
    configString += 'h' + (2*radius - width);
    if (includes(corners, 'bottom-left')) {
      configString += 'a' + radius + ',' + radius + ' 0 0 1 ' + -radius + ',' + -radius;
    } else {
      configString += 'h' + -radius; configString += 'v' + -radius;
    }
    configString += 'v' + (2*radius - height);
    if (includes(corners, 'top-left')) {
      configString += 'a' + radius + ',' + radius + ' 0 0 1 ' + radius + ',' + -radius;
    } else {
      configString += 'v' + -radius; configString += 'h' + radius;
    }
    configString += 'z';
  }

  return configString;
};

export const scale = ({
  width='bad', height='bad', bounds='bad',
  scaleFactor = 1
}) => {
  if(width === 'bad' || height === 'bad' || bounds === 'bad') {
    return 1;
  }

  const maxWidth = (bounds.xMax - bounds.xMin) / width;
  const maxHeight = (bounds.yMax - bounds.yMin) / height;
  const longestSide = Math.max(maxWidth, maxHeight);
  return scaleFactor / longestSide;
};

export const scatterScaleLinear = (axis) => {
  return scaleLinear().domain(minMax(axis)).range(axis.pixelRange);
};

export const scatterScaleOrdinal = (axis) => {
  return scaleBand().domain(axis.valueRange).range(axis.pixelRange).padding(0.2);
};

export const scatterScale = (axis) => {
  return axis.scaleType === 'ordinal' ? scatterScaleOrdinal(axis) : scatterScaleLinear(axis);
};

export const tickValuesLinear = (axis) => {
  let range = minMax(axis);
  range.splice(1, 0, sum(range)/size(range));
  return range;
};

export const tickValues = (axis) => {
  return axis.scaleType === 'ordinal' ? axis.valueRange : tickValuesLinear(axis);
};

export const translation = ({
  width = 'bad', height = 'bad', bounds = 'bad',
  translationScale = 1
}) => {
  if(width === 'bad' || height === 'bad' || bounds === 'bad') {
    return [0, 0];
  }

  const xTranslation = (width - translationScale * (bounds.xMax + bounds.xMin));
  const yTranslation = (height - translationScale * (bounds.yMax + bounds.yMin));
  return [xTranslation / 2, yTranslation / 2];
};

export const xyify = (bounds) => {
  if (!isArray(bounds)) {
    return bounds;
  }

  return {
    xMin: bounds[0][0],
    yMin: bounds[0][1],
    xMax: bounds[1][0],
    yMax: bounds[1][1]
  };
};
